import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ArrowIcon } from '../Icons';

/**
 * DropdownToggle — the button that opens/closes the multiselect dropdown.
 *
 * Accessibility:
 *  • role="combobox" indicates an input with a popup.
 *  • aria-expanded reflects open/closed state.
 *  • aria-controls points to the listbox ID.
 *
 * Props:
 * @param {boolean} open
 *   Whether the dropdown is currently open.
 * @param {boolean} [disabled=false]
 *   Disables interaction and styles when true.
 * @param {number} count
 *   Number of items currently selected (for display).
 * @param {string} label
 *   The field label, used in placeholder and summary text.
 * @param {() => void} onToggle
 *   Callback to open/close the dropdown.
 */
const DropdownToggle = forwardRef(({ open, disabled, count, label, onToggle }, ref) => (
  <button
    ref={ref}
    type="button"
    role="combobox"
    aria-expanded={open}
    aria-controls="multiselect-list"
    disabled={disabled}
    className={clsx(
      'w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md text-md bg-white min-h-[2.5rem]',
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    )}
    onClick={onToggle}
  >
    <span className="text-gray-500">
      {count ? `${count} ${label} selected` : `Select ${label}`}
    </span>
    <ArrowIcon className={clsx('transition-transform', open && 'rotate-180')} />
  </button>
));

DropdownToggle.displayName = 'DropdownToggle';

DropdownToggle.propTypes = {
  open: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  count: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default DropdownToggle;
